import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import toast, { Toaster } from "react-hot-toast";
import trustLogo from "../assets/trust_logo.png";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);

  async function handleSubmit(e) {
  e.preventDefault();
  if (!email || !password) return toast.error("Please fill in all fields");
  try {
    setLoading(true);
    const cred = await login(email, password);
    
    // Check account status in Firestore
    const { doc, getDoc } = await import("firebase/firestore");
    const { db } = await import("../firebase");
    const snap = await getDoc(doc(db, "users", cred.user.uid));
    
    if (snap.exists() && snap.data().status === "inactive") {
      await import("firebase/auth").then(({ getAuth, signOut }) => signOut(getAuth()));
      toast.error("⛔ Your account has been disabled. Please contact admin.", { duration:5000 });
      setLoading(false);
      return;
    }

    toast.success("Welcome back!");
    navigate("/dashboard");
  } catch (err) {
    if (err.code === "auth/user-not-found" || err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
      toast.error("Invalid email or password");
    } else if (err.code === "auth/too-many-requests") {
      toast.error("Too many attempts. Please try again later.");
    } else {
      toast.error("Login failed. Please try again.");
    }
  } finally { setLoading(false); }
}

  return (
    <div style={s.root}>
      <Toaster position="top-center"/>
      <div style={s.card}>
        {/* Logo */}
        <div style={s.logoWrap}>
          <img src={trustLogo} alt="HTS" style={s.logo}/>
        </div>
        <h1 style={s.title}>Member Portal</h1>
        <p style={s.sub}>Help Them Shine Trust</p>

        <form onSubmit={handleSubmit} style={s.form}>
          {/* Email */}
          <div style={s.field}>
            <label style={s.label}>Email Address</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              style={s.input}
              autoComplete="email"
              required
            />
          </div>

          {/* Password */}
          <div style={s.field}>
            <label style={s.label}>Password</label>
            <div style={{position:"relative"}}>
              <input
                type={showPass ? "text" : "password"}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                style={{...s.input, paddingRight:44}}
                autoComplete="current-password"
                required
              />
              <button type="button" onClick={() => setShowPass(p => !p)}
                style={s.eyeBtn}>
                {showPass ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          {/* Forgot Password */}
          <div style={{textAlign:"right", marginBottom:20}}>
            <Link to="/forgot-password" style={s.link}>Forgot password?</Link>
          </div>

          {/* Submit */}
          <button type="submit" disabled={loading} style={{...s.btn, opacity: loading ? 0.7 : 1}}>
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <p style={s.footer}>
          © {new Date().getFullYear()} Help Them Shine Trust · All rights reserved
        </p>
      </div>
    </div>
  );
}

const s = {
  root: {
    minHeight: "100vh",
    background: "#0a0908",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
  },
  card: {
    background: "#111009",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 20,
    padding: "40px 36px",
    width: "100%",
    maxWidth: 420,
    textAlign: "center",
  },
  logoWrap: {
    marginBottom: 20,
  },
  logo: {
    width: 72,
    height: 72,
    objectFit: "contain",
    borderRadius: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: 800,
    color: "#fef3e2",
    margin: "0 0 6px",
    fontFamily: "'Playfair Display', serif",
  },
  sub: {
    fontSize: 13,
    color: "#78716c",
    marginBottom: 32,
  },
  form: {
    textAlign: "left",
  },
  field: {
    marginBottom: 18,
  },
  label: {
    display: "block",
    fontSize: 12,
    fontWeight: 700,
    color: "#a8a29e",
    marginBottom: 7,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  input: {
    width: "100%",
    padding: "12px 14px",
    background: "#1a1714",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 10,
    color: "#fef3e2",
    fontSize: 14,
    fontFamily: "inherit",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.2s",
  },
  eyeBtn: {
    position: "absolute",
    right: 12,
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    color: "#78716c",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  link: {
    fontSize: 12,
    color: "#f59e0b",
    textDecoration: "none",
    fontWeight: 600,
  },
  btn: {
    width: "100%",
    padding: "14px",
    background: "#f59e0b",
    color: "#0a0908",
    border: "none",
    borderRadius: 12,
    fontSize: 15,
    fontWeight: 800,
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "opacity 0.2s",
  },
  footer: {
    marginTop: 28,
    fontSize: 11,
    color: "#52504d",
  }
};
